import { ReactNode, useState } from 'react';

import { randomUUID } from 'expo-crypto';
import { useRouter } from 'expo-router';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { IconButton } from 'native-base';
import { useIntl } from 'react-intl';
import { StyleSheet, Text } from 'react-native';
import { Icon } from 'ucl/components/icon/Icon';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { IconElementProp } from 'components/action-button/types';
import ConfirmDialog from 'components/confirm-dialog';
import { FavoriteIcon } from 'components/favorite-icon';
import { appendObjectToQueryString, convertQueryStringToObject } from 'navigation/utils';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { primitive } from 'styles/constants/primitives';
import { isCartEntryPicker } from 'utils/cart/is-cart-type';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';
import noop from 'utils/noop';

const CartEntryActionButton = ({
  disabled,
  accessibilityLabel,
  testID,
  icon,
  children,
  onPress,
}: {
  disabled?: boolean;
  accessibilityLabel: string;
  testID: string;
  icon?: IconElementProp;
  children?: ReactNode;
  onPress?: () => void;
}) => {
  return (
    <ActionButton
      variant={ActionButtonVariants.TEXT_ONLY}
      padding={0}
      margin={0}
      keepIconSize
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      onPress={onPress}
      leftIcon={icon}
      disabled={disabled}
      color={disabled ? primitive.$blackOpacity50 : primitive.bk.$bbqBrown}
    >
      {children}
    </ActionButton>
  );
};

export const CartEntryDeleteButton = ({
  cartEntry,
  variant,
  disabled,
}: {
  cartEntry: ICartEntry;
  variant: 'textOnly' | 'iconOnly';
  disabled?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const { removeItemFromMinimalCart } = useMinimalCart();
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);

  const handleRemoveItem = () => {
    hapticNotification({ type: HapticsNotificationType.WARNING });
    setShowDeleteConfirmationDialog(true);
  };

  const handleConfirmRemoveItem = () => {
    setShowDeleteConfirmationDialog(false);
    removeItemFromMinimalCart(cartEntry);
  };

  return (
    <>
      {variant === 'textOnly' && (
        <CartEntryActionButton
          accessibilityLabel={`${formatMessage({ id: 'remove' })} ${cartEntry.name} `}
          testID="remove-cart-entry-button"
          onPress={handleRemoveItem}
          disabled={disabled}
        >
          <Text style={styles.removeText}>{formatMessage({ id: 'remove' })}</Text>
        </CartEntryActionButton>
      )}
      {variant === 'iconOnly' && (
        <CartEntryActionButton
          accessibilityLabel={`${formatMessage({ id: 'remove' })} ${cartEntry.name} `}
          testID="remove-cart-entry-button"
          onPress={handleRemoveItem}
          icon={<Icon variant="bkRemove" size="md" />}
          disabled={disabled}
        />
      )}
      <ConfirmDialog
        showDialog={showDeleteConfirmationDialog}
        heading={formatMessage({ id: 'removeItem' })}
        body={formatMessage(
          { id: 'removeItemFromCart' },
          {
            item: cartEntry.name,
            offerText: '',
          }
        )}
        confirmLabel={formatMessage({ id: 'okay' })}
        cancelLabel={formatMessage({ id: 'cancel' })}
        onConfirm={handleConfirmRemoveItem}
        onCancel={() => {
          setShowDeleteConfirmationDialog(false);
        }}
        modalAppearanceEventMessage={''}
      />
    </>
  );
};

/* this uses maxCartQuantityMet */
export const CartEntryDuplicateButton = ({
  cartEntry,
  disabled,
}: {
  cartEntry: ICartEntry;
  disabled?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const { indexedMenu } = useUnifiedMenuContext();
  const { cartEntries, addItemToMinimalCart } = useMinimalCart();
  const menuEntry = indexedMenu.find(cartEntry.id);
  const maxCartQuantity = menuEntry?.quantity?.max;
  const quantityOnCart = cartEntries.reduce(
    (acc, entry) => (entry.id === cartEntry.id ? acc + entry.quantity : acc),
    0
  );

  // TODO modify it  for oneMenu
  // const attributes = {
  //   'Product Id': cartEntry.id,
  //   'Product Name': cartEntry.name,
  //   'Edit Url': productWizardEditUrl,
  //   'Is Reorder': booleanToString(false),
  //   'Is Offer': booleanToString(isOfferCartEntry(cartEntry)),
  // };
  // logRBIProductsDetailsEvent({
  //   name: CustomEventNames.EDIT_CART,
  //   products: [cartEntry as ICartEntryLoggable],
  //   attributes,
  // });

  const handleDuplicate = () => {
    const newCartEntry = {
      ...cartEntry,
      cartId: randomUUID(),
    };
    addItemToMinimalCart(newCartEntry);
  };

  if (cartEntry.type === MinimalCartEntryType.incentive) {
    return null;
  }
  return (
    <CartEntryActionButton
      disabled={disabled || quantityOnCart >= maxCartQuantity}
      accessibilityLabel={`${formatMessage({ id: 'duplicate' })} ${cartEntry.name} `}
      testID="duplicate-cart-entry-button"
      icon={<Icon name="custom:DuplicateItem" size="md" />}
      onPress={handleDuplicate}
    />
  );
};

export const CartEntryEditButton = ({
  cartEntry,
  disabled,
}: {
  cartEntry: ICartEntry;
  disabled?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const router = useRouter();

  const handleEdit = () => {
    const baseUrl = cartEntry.url.split('?')[0];

    // This is to support offer pickers editing
    const queryParams = convertQueryStringToObject<{ offerId?: string }>(
      cartEntry.url.replace(`${baseUrl}?`, '')
    );
    const offerIdParam = queryParams.offerId;

    const productWizardEditUrl = appendObjectToQueryString(baseUrl, {
      isEdit: true,
      cartIdEditing: cartEntry.cartId,
      isFromCart: true,
      isProductReviewFromCart: isCartEntryPicker(cartEntry.url),
      ...(offerIdParam ? { offerId: offerIdParam } : {}),
    });

    // TODO modify it  for oneMenu
    // const attributes = {
    //   'Product Id': cartEntry.id,
    //   'Product Name': cartEntry.name,
    //   'Edit Url': productWizardEditUrl,
    //   'Is Reorder': booleanToString(false),
    //   'Is Offer': booleanToString(isOfferCartEntry(cartEntry)),
    // };
    // logRBIProductsDetailsEvent({
    //   name: CustomEventNames.EDIT_CART,
    //   products: [cartEntry as ICartEntryLoggable],
    //   attributes,
    // });

    router.push(productWizardEditUrl);
  };

  return (
    <CartEntryActionButton
      disabled={disabled}
      accessibilityLabel={`${formatMessage({ id: 'edit' })} ${cartEntry.name} `}
      testID="edit-cart-entry-button"
      onPress={handleEdit}
    >
      <Text style={styles.removeText}>{formatMessage({ id: 'edit' })}</Text>
    </CartEntryActionButton>
  );
};

export const CartEntryFabButton = ({
  cartEntryId,
  isFavorite,
  disabled,
  loading,
}: {
  cartEntryId: string;
  isFavorite?: boolean;
  disabled?: boolean;
  loading?: boolean;
}) => {
  const { formatMessage } = useIntl();

  const accessibilityLabel = isFavorite
    ? formatMessage({ id: 'removeFavorite' })
    : formatMessage({ id: 'addFavorite' });

  return (
    <IconButton
      variant="ghost"
      onPress={noop}
      testID="favorite-icon"
      disabled={disabled || loading}
      _web={{
        cursor: disabled ? 'not-allowed' : 'default',
      }}
      alignSelf="flex-start"
      position="absolute"
      right={0}
      top={-2}
      zIndex={1}
      icon={
        <FavoriteIcon isFavorite={isFavorite} disabled={disabled || loading} loading={loading} />
      }
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ checked: true }}
    />
  );
};

const styles = StyleSheet.create({
  removeText: {
    fontSize: 14,
    fontWeight: 'bold',
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.bk.$bbqBrown,
  },
});
